import { useState, useEffect } from 'react';

import LoadStatus from 'config/load_status';

import useAuthentication from 'domain/authentication/useAuthentication';
import readRecipe, { NotFoundError } from 'domain/recipe/usecases/read_recipe';
import wasRecipeBookmarked from './usecases/was_recipe_bookmarked';
import wasRecipeLiked from 'domain/recipe/usecases/was_recipe_liked';

import delayed from 'utils/delayed';

const ReadRecipeStatus = {
  ...LoadStatus,
  NOT_FOUND: 'not_found',
};

const useReadRecipe = (id, delayInMS = 800) => {
  if (!id) throw new Error('id cannot be empty');

  const { user } = useAuthentication();

  const [recipe, setRecipe] = useState(null);
  const [loadRecipeStatus, setLoadRecipeStatus] = useState(
    ReadRecipeStatus.LOADING
  );
  const [recipeLiked, setRecipeLiked] = useState(false);
  const [recipeBookmarked, setRecipeBookmarked] = useState(false);

  useEffect(() => {
    if (loadRecipeStatus !== ReadRecipeStatus.LOADING) return;

    const readData = async () => {
      try {
        const recipeModel = await delayed(readRecipe(id), delayInMS);

        if (user) {
          const [liked, bookmarked] = await Promise.all([
            wasRecipeLiked(id, user),
            wasRecipeBookmarked(id, user),
          ]);

          setRecipeLiked(liked);
          setRecipeBookmarked(bookmarked);
        }

        setRecipe(recipeModel);
        setLoadRecipeStatus(ReadRecipeStatus.DONE);
      } catch (error) {
        if (error instanceof NotFoundError) {
          setLoadRecipeStatus(ReadRecipeStatus.NOT_FOUND);
          return;
        }

        setLoadRecipeStatus(ReadRecipeStatus.ERROR);
        throw error;
      }
    };
    readData();
  }, [loadRecipeStatus, id, user, delayInMS]);

  const loadRecipe = () => {
    if (loadRecipeStatus === ReadRecipeStatus.DONE) return;
    setLoadRecipeStatus(ReadRecipeStatus.LOADING);
  };

  return {
    recipe,
    isLoading: loadRecipeStatus === ReadRecipeStatus.LOADING,
    hasFailed: loadRecipeStatus === ReadRecipeStatus.ERROR,
    hasLoaded: loadRecipeStatus === ReadRecipeStatus.DONE,
    notFound: loadRecipeStatus === ReadRecipeStatus.NOT_FOUND,
    loadRecipe,
    recipeLiked,
    recipeBookmarked,
  };
};

export default useReadRecipe;
